import { AsyncPipe, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OnboardingDialogResult } from '@app/+authenticated/shared/onboarding/onboarding-dialog/onboarding-dialog.model';
import { OnboardingUtils } from '@app/+onboarding/onboarding.utils';
import { BoldPipe } from '@app/pipes/bold.pipe';
import { KnowledgeBaseArticleLinkModule } from '@app/pipes/knowledge-base-article-link.module';
import { UserGuideType } from '@app/reducers/orm/user-guide/user-guide.model';
import { TrackingService } from '@app/services/tracking.service';
import { SmbHospitalityOnboardingChecklistStep } from '@app/shared/onboarding/onboarding-checklist.model';
import { OnboardingChecklistService } from '@app/shared/onboarding/onboarding-checklist.service';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { selectAuthUserEmail } from '@reducers/auth/auth.selectors';
import { AppState } from '@reducers/interfaces';
import { ButtonThemePalette } from '@sb/ui/button';
import { DIALOG_DATA, DialogActivationComponent, DialogRef } from '@sb/ui/dialog';
import { SbFormFieldComponent } from '@sb/ui/form-field';
import { InputDirective } from '@sb/ui/input';
import { RadioBadgeComponent } from '@sb/ui/radio-badge';
import { RadioBadgeGroupComponent } from '@sb/ui/radio-badge-group';
import { finalize, switchMap } from 'rxjs';

import { OnboardingChapterModule } from '../components/chapter.module';
import { PayrollDialogStep, payrollDialogStepProgressMap } from './payroll.model';
import { OnboardingPayrollService } from './payroll.service';

type PayrollToolOption =
  | 'AFAS Software'
  | 'Buddee'
  | 'Connexie'
  | 'Datev'
  | 'e-UUR'
  | 'Easylon'
  | 'Employes'
  | 'Exact Online'
  | 'HR & Salaris Gemak'
  | 'Loket.nl'
  | 'SD Worx'
  | 'Visma'
  | 'Nmbrs'
  | 'other'
  | 'unknown_provider'
  | 'none';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'payroll-dialog',
  imports: [
    DialogActivationComponent,
    FormsModule,
    NgStyle,
    TranslatePipe,
    OnboardingChapterModule,
    RadioBadgeGroupComponent,
    RadioBadgeComponent,
    KnowledgeBaseArticleLinkModule,
    InputDirective,
    SbFormFieldComponent,
    AsyncPipe,
    BoldPipe,
  ],
  providers: [OnboardingPayrollService],
  templateUrl: './payroll-dialog.component.html',
})
export class PayrollDialogComponent {
  eventData = inject(DIALOG_DATA);
  private dialogRef = inject(DialogRef);
  private payrollService = inject(OnboardingPayrollService);
  private store = inject<Store<AppState>>(Store);
  private translate = inject(TranslateService);
  private trackingService = inject(TrackingService);
  private onboardingUtils = inject(OnboardingUtils);
  private onboardingChecklist = inject(OnboardingChecklistService);

  ButtonThemePalette = ButtonThemePalette;

  PayrollDialogStep = PayrollDialogStep;
  currentStep = PayrollDialogStep.TEASER;
  currentStepProgress = payrollDialogStepProgressMap[this.currentStep];
  steps = 3;
  nextButtonLoading = false;
  skipButtonLoading = false;
  email$ = this.store.select(selectAuthUserEmail);

  selectedPayrollTool: PayrollToolOption;
  payrollToolOptions: { value: PayrollToolOption; label?: string }[] = [
    { value: 'AFAS Software' },
    { value: 'Buddee' },
    { value: 'Connexie' },
    { value: 'Datev' },
    { value: 'e-UUR' },
    { value: 'Easylon' },
    { value: 'Employes' },
    { value: 'Exact Online' },
    { value: 'HR & Salaris Gemak' },
    { value: 'Loket.nl' },
    { value: 'SD Worx' },
    { value: 'Visma' },
    { value: 'Nmbrs' },
    { value: 'other', label: this.translate.instant('Other tool') },
    { value: 'unknown_provider', label: this.translate.instant("I don't know which tool") },
  ];
  payrollTool: string;
  skipEmail = false;

  private eventTracked = false;

  get chapterCTAs() {
    return this.onboardingUtils.chapterCTAs();
  }

  onSelectPayrollToolNext() {
    switch (this.selectedPayrollTool) {
      case 'other':
        this.goToStep(PayrollDialogStep.OTHER_TOOL);
        break;
      case 'unknown_provider':
        this.goToStep(PayrollDialogStep.UNKNOWN_TOOL);
        break;
      default:
        this.goToStep(PayrollDialogStep.SPECIFIC_TOOL);
    }
  }

  onNoPayrollToolSelected() {
    this.selectedPayrollTool = 'none';
    this.goToStep(PayrollDialogStep.NO_TOOL);
  }

  goToStep(step: PayrollDialogStep, skipEmail?: boolean, clickedButton: 'next' | 'skip' = 'next') {
    if (this.currentStep === PayrollDialogStep.TEASER && !this.eventTracked) {
      this.trackingService.event('Onboarding Payroll Started', this.eventData);
      this.eventTracked = true;
    }

    if (step === PayrollDialogStep.SUMMARY) {
      this.skipEmail = skipEmail ?? false;
      this.savePreferredPayrollIntegration(clickedButton);
    } else {
      this.currentStep = step;
      this.currentStepProgress = payrollDialogStepProgressMap[this.currentStep];
    }
  }

  goBack() {
    const intermediateSteps = [
      PayrollDialogStep.OTHER_TOOL,
      PayrollDialogStep.UNKNOWN_TOOL,
      PayrollDialogStep.SPECIFIC_TOOL,
      PayrollDialogStep.NO_TOOL,
    ];
    if (intermediateSteps.includes(this.currentStep)) {
      this.goToStep(PayrollDialogStep.SELECT_PAYROLL_TOOL);
      return;
    }

    this.goToStep(this.currentStep - 1);
  }

  close(result: OnboardingDialogResult) {
    this.dialogRef.close(result);
  }

  private savePreferredPayrollIntegration(clickedButton: 'next' | 'skip') {
    this.nextButtonLoading = clickedButton === 'next';
    this.skipButtonLoading = clickedButton === 'skip';

    let preferredPayrollIntegration: string = this.selectedPayrollTool;

    if (this.selectedPayrollTool === 'other') {
      preferredPayrollIntegration = clickedButton === 'skip' ? 'not_specified' : this.payrollTool;
    }

    void this.payrollService
      .savePreferredPayrollIntegration(preferredPayrollIntegration, this.skipEmail)
      .pipe(
        switchMap(() =>
          this.onboardingChecklist.completeStep(
            UserGuideType.SELF_ONBOARDING_CHECKLIST,
            SmbHospitalityOnboardingChecklistStep.PAYROLL,
          ),
        ),
        finalize(() => {
          this.nextButtonLoading = false;
          this.skipButtonLoading = false;
        }),
      )
      .subscribe(() => {
        this.trackingService.event('Onboarding Payroll Completed', this.eventData);
        this.currentStep = PayrollDialogStep.SUMMARY;
      });
  }
}
